import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FeatherArrowLeft, FeatherSearch, FeatherX } from '../components/ui/icons';
import TaskConversationViewer from '../components/Manager/TaskConversationViewer';
import { managerApi } from '../services/api';
import { isManager } from '../utils/roleUtils';
import api from '../services/api';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import './TaskConversations.css';

const TaskConversations = () => {
    const navigate = useNavigate();
    const [userIsManager, setUserIsManager] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [filters, setFilters] = useState({
        taskId: '',
        userId: '',
        projectId: '',
        dateFrom: '',
        dateTo: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        checkUserRole();
        loadUsers();
        loadProjects();
    }, []);

    useEffect(() => {
        if (userIsManager) {
            loadConversations();
        }
    }, [userIsManager, filters]);

    const checkUserRole = async () => {
        try {
            const response = await api.get('/api/auth/role');
            const userRole = response.data?.role;
            setUserIsManager(isManager(userRole));
            if (!isManager(userRole)) {
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Error checking user role:', err);
            navigate('/dashboard');
        }
    };

    const loadUsers = async () => {
        try {
            const response = await managerApi.getUsers();
            if (response) {
                setUsers(response);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadProjects = async () => {
        try {
            const { projectApi } = await import('../services/api');
            const response = await projectApi.getAll();
            if (response) {
                setProjects(Array.isArray(response) ? response : []);
            }
        } catch (error) {
            console.error('Error loading projects:', error);
        }
    };

    const loadConversations = async () => {
        setLoading(true);
        try {
            const response = await managerApi.getAllConversations(filters);
            if (response.success && response.data) {
                setConversations(response.data);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            taskId: '',
            userId: '',
            projectId: '',
            dateFrom: '',
            dateTo: ''
        });
    };

    const hasActiveFilters = Object.values(filters).some(value => value !== '');

    if (!userIsManager) {
        return null;
    }

    return (
        <DefaultPageLayout>
            <div className="task-conversations-page">
                <div className="task-conversations-header">
                    <div className="task-conversations-header-top">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherArrowLeft />}
                            onClick={() => navigate('/dashboard')}
                            title="Back to Dashboard"
                        />
                        <h1 className="task-conversations-title">Task Conversations</h1>
                    </div>
                    <div className="task-conversations-header-actions">
                        <Button
                            variant={showFilters ? 'primary' : 'neutral-secondary'}
                            icon={<FeatherSearch />}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            Filters
                        </Button>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="task-conversations-filters">
                        <div className="task-conversations-filters-grid">
                            <div className="task-conversations-filter-group">
                                <label>Task ID</label>
                                <input
                                    type="text"
                                    placeholder="Enter task ID"
                                    value={filters.taskId}
                                    onChange={(e) => handleFilterChange('taskId', e.target.value)}
                                />
                            </div>
                            <div className="task-conversations-filter-group">
                                <label>User</label>
                                <select
                                    value={filters.userId}
                                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                                >
                                    <option value="">All Users</option>
                                    {users.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.display_name || user.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="task-conversations-filter-group">
                                <label>Project</label>
                                <select
                                    value={filters.projectId}
                                    onChange={(e) => handleFilterChange('projectId', e.target.value)}
                                >
                                    <option value="">All Projects</option>
                                    {projects.map(project => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="task-conversations-filter-group">
                                <label>Date From</label>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                />
                            </div>
                            <div className="task-conversations-filter-group">
                                <label>Date To</label>
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                />
                            </div>
                        </div>
                        {hasActiveFilters && (
                            <div className="task-conversations-filters-actions">
                                <Button
                                    variant="neutral-secondary"
                                    icon={<FeatherX />}
                                    onClick={clearFilters}
                                >
                                    Clear Filters
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <div className="task-conversations-content">
                    {loading ? (
                        <div className="task-conversations-loading">
                            <p>Loading conversations...</p>
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className="task-conversations-empty">
                            <p>No conversations found.</p>
                            {hasActiveFilters && (
                                <Button
                                    variant="neutral-secondary"
                                    onClick={clearFilters}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="task-conversations-layout">
                            <div className="task-conversations-list">
                                <h3 className="task-conversations-list-title">
                                    Conversations ({conversations.length})
                                </h3>
                                <div className="task-conversations-list-items">
                                    {conversations.map((conv, index) => (
                                        <div
                                            key={conv.id || index}
                                            className={`task-conversations-list-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                                            onClick={() => setSelectedConversation(conv)}
                                        >
                                            <div className="task-conversations-list-item-header">
                                                <h4 className="task-conversations-list-item-title">
                                                    {conv.taskTitle || 'Untitled Task'}
                                                </h4>
                                                {conv.project && (
                                                    <span className="task-conversations-list-item-project">
                                                        {conv.project.abbreviation || conv.project.name}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="task-conversations-list-item-meta">
                                                {conv.user && (
                                                    <span className="task-conversations-list-item-user">
                                                        {conv.user.name}
                                                    </span>
                                                )}
                                                <span className="task-conversations-list-item-date">
                                                    {new Date(conv.workPlanDate).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                            <div className="task-conversations-list-item-preview">
                                                {conv.conversationMessages?.[0]?.content?.substring(0, 150) || 'No messages'}
                                                {conv.conversationMessages?.[0]?.content?.length > 150 ? '...' : ''}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="task-conversations-viewer">
                                {selectedConversation ? (
                                    <>
                                        <div className="task-conversations-viewer-header">
                                            <h3>Conversation Details</h3>
                                            <Button
                                                variant="neutral-tertiary"
                                                icon={<FeatherX />}
                                                onClick={() => setSelectedConversation(null)}
                                            />
                                        </div>
                                        <div className="task-conversations-viewer-content">
                                            <TaskConversationViewer
                                                conversation={selectedConversation.conversationMessages || []}
                                                taskTitle={selectedConversation.taskTitle}
                                                taskDescription={selectedConversation.taskDescription}
                                                userName={selectedConversation.user?.name}
                                                workPlanDate={selectedConversation.workPlanDate}
                                                taskStatusAtTime={selectedConversation.taskStatusAtTime}
                                                project={selectedConversation.project}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="task-conversations-viewer-empty">
                                        <p>Select a conversation to view details</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DefaultPageLayout>
    );
};

export default TaskConversations;

